/**
 * Toasts: sonner keeps the queue, the timer, and the live region; each toast
 * renders finixt-ui's Toast. The API is the one the console's fifty call
 * sites already use, so a page changes its import only.
 *
 * A toast confirms something that already happened. It never asks a
 * question; that is a Dialog.
 */
import { toast } from 'sonner';

import { Toast } from './finixt.js';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastApi {
  show: (input: {
    variant?: ToastVariant;
    title: string;
    description?: string;
    duration?: number;
  }) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const TONE: Record<ToastVariant, 'ok' | 'danger' | 'neutral'> = {
  success: 'ok',
  error: 'danger',
  info: 'neutral',
};

function emit(variant: ToastVariant, title: string, description?: string, duration?: number) {
  toast.custom(
    (id) => (
      <Toast
        tone={TONE[variant]}
        onClose={() => toast.dismiss(id)}
        message={
          <>
            <span style={{ fontWeight: 'var(--weight-semibold)' }}>{title}</span>
            {description ? (
              <span style={{ display: 'block', marginTop: 2, color: 'var(--grey-500)' }}>
                {description}
              </span>
            ) : null}
          </>
        }
      />
    ),
    { ...(duration === undefined ? {} : { duration }) },
  );
}

const api: ToastApi = {
  show: ({ variant = 'info', title, description, duration }) =>
    emit(variant, title, description, duration),
  success: (title, description) => emit('success', title, description),
  error: (title, description) => emit('error', title, description),
  info: (title, description) => emit('info', title, description),
};

export function useToast(): ToastApi {
  return api;
}
